/**
 * Hook to check downgrade warning status
 * Returns whether user should see the downgrade warning dialog
 */

import { useState, useEffect } from 'react';
import { supabase } from '@config/supabase';
import { useAuthStore } from '@stores/useAuthStore';

interface DowngradeWarningStatus {
  showDialog: boolean;
  storageUsed: number;
  storageLimit: number;
  daysRemaining: number;
  isLoading: boolean;
}

export const useDowngradeWarning = (): DowngradeWarningStatus => {
  const user = useAuthStore((state) => state.user);
  const [status, setStatus] = useState<DowngradeWarningStatus>({
    showDialog: false,
    storageUsed: 0,
    storageLimit: 0,
    daysRemaining: 0,
    isLoading: true,
  });

  useEffect(() => {
    if (!user?.id) {
      setStatus((prev) => ({ ...prev, isLoading: false }));
      return;
    }

    const checkDowngradeWarning = async () => {
      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('storage_used, storage_limit, downgrade_warning_started_at, downgrade_warning_dismissed, is_suspended')
          .eq('id', user.id)
          .single();

        if (error || !profile) {
          console.error('Error checking downgrade warning:', error);
          setStatus((prev) => ({ ...prev, isLoading: false }));
          return;
        }

        // Only show if:
        // 1. Warning started (downgrade_warning_started_at is set)
        // 2. Not dismissed
        // 3. Not suspended
        // 4. Still in overflow (storage_used > storage_limit)
        const hasWarning = !!profile.downgrade_warning_started_at;
        const isDismissed = profile.downgrade_warning_dismissed === true;
        const isSuspended = profile.is_suspended === true;
        const isOverflow = (profile.storage_used || 0) > (profile.storage_limit || 0);

        if (hasWarning && !isDismissed && !isSuspended && isOverflow) {
          // Calculate days remaining (3 days from warning start)
          const warningStarted = new Date(profile.downgrade_warning_started_at);
          const now = new Date();
          const daysElapsed = Math.floor((now.getTime() - warningStarted.getTime()) / (1000 * 60 * 60 * 24));
          const daysRemaining = Math.max(0, 3 - daysElapsed);

          setStatus({
            showDialog: true,
            storageUsed: profile.storage_used || 0,
            storageLimit: profile.storage_limit || 0,
            daysRemaining,
            isLoading: false,
          });
        } else {
          setStatus({
            showDialog: false,
            storageUsed: profile.storage_used || 0,
            storageLimit: profile.storage_limit || 0,
            daysRemaining: 0,
            isLoading: false,
          });
        }
      } catch (error) {
        console.error('Error in useDowngradeWarning:', error);
        setStatus((prev) => ({ ...prev, isLoading: false }));
      }
    };

    checkDowngradeWarning();

    // Subscribe to profile changes
    const subscription = supabase
      .channel(`downgrade-warning-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`,
        },
        () => {
          checkDowngradeWarning();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user?.id]);

  return status;
};

